import { executeQuery } from './salesforce/client';
import { EXCLUDE_DEACTIVATED_FIRMS } from '../constants';

/**
 * Elapsed-time (in days) between a firm's creation and a plan milestone, for
 * firms hitting that milestone on/after `startDate`. Returns one record per
 * distinct firm shaped `{ [milestoneField]: date, elapsedDays }` so the weekly-
 * metrics engine can bucket by the milestone date and average `elapsedDays`.
 *
 * Both milestones are sourced from Encore_Firm__c and de-duped by the firm id so
 * seat/dupe rows don't skew the average.
 */
async function fetchTimeToPlan(
  object: string,
  createdField: string,
  milestoneField: string,
  firmIdField: string,
  exclusion: string,
  startDate: string,
  signal?: AbortSignal
): Promise<Record<string, unknown>[]> {
  const soql = `SELECT ${firmIdField}, ${createdField}, ${milestoneField} FROM ${object} WHERE ${firmIdField} != null${exclusion} AND ${createdField} != null AND ${milestoneField} != null AND ${milestoneField} >= ${startDate}`;

  const rows = await executeQuery<Record<string, unknown>>(soql, signal);

  const seen = new Set<string>();
  const out: Record<string, unknown>[] = [];
  for (const r of rows) {
    const firmId = r[firmIdField] as string | null;
    if (!firmId || seen.has(firmId)) continue;
    seen.add(firmId);

    const created = new Date(String(r[createdField]));
    const milestone = new Date(String(r[milestoneField]));
    const elapsedDays = (milestone.getTime() - created.getTime()) / 86_400_000;
    if (isNaN(elapsedDays) || elapsedDays < 0) continue;

    out.push({ [milestoneField]: r[milestoneField], elapsedDays });
  }
  return out;
}

export function fetchTimeToFirstPlan(startDate: string, signal?: AbortSignal) {
  return fetchTimeToPlan(
    'Encore_Firm__c',
    'EEP_Firm_Created_Date__c',
    'First_Plan_Date__c',
    'EEP_Firm_ID__c',
    EXCLUDE_DEACTIVATED_FIRMS,
    startDate,
    signal
  );
}

export function fetchTimeToSeventhPlan(startDate: string, signal?: AbortSignal) {
  return fetchTimeToPlan(
    'Encore_Firm__c',
    'EEP_Firm_Created_Date__c',
    'Seventh_Plan_Date__c',
    'EEP_Firm_ID__c',
    EXCLUDE_DEACTIVATED_FIRMS,
    startDate,
    signal
  );
}
